/**
 * `<KeyboardShortcuts>` — the global single-letter surface-navigation handler
 * (UX-reconciliation F3 — docs/refactor-ux/phase1/DECISIONS.md §1.3).
 *
 * Reads the shortcut map from the `ShellMount` nav registry (`NavItem.kbd`). When a
 * matching bare keystroke fires on the document, navigates to the item's route.
 *
 * **Guards** (verbatim from the standalone's `11_*.jsx` handler — known-good design):
 *   1. The event target's `tagName` must not be `INPUT` or `TEXTAREA` — focused
 *      form fields keep their keystrokes.
 *   2. The target's `isContentEditable` must be `false` — covers CodeMirror's
 *      contenteditable surface, the chat composer, anything else editable.
 *   3. No `Meta` / `Ctrl` / `Alt` modifier may be held — chord shortcuts (⌘K palette,
 *      Cmd-Z undo, etc.) stay reserved for their handlers.
 *   4. The CommandPalette must not be open — its own search box would catch keys first
 *      via guard 1, but belt-and-braces.
 *
 * Renders nothing — pure effect. Mount once inside `<AppShell>`, after the
 * `ShellMountProvider` and inside the router context (uses `useNavigate`).
 */

import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUiStore } from '@/stores';
import { useShellMountSnapshot } from './ShellMountContext';

export function KeyboardShortcuts(): null {
  const mount = useShellMountSnapshot();
  const navigate = useNavigate();
  const paletteOpen = useUiStore((s) => s.commandPaletteOpen);

  useEffect(() => {
    // Build the kbd → route lookup from the current registry snapshot. Re-runs on
    // registration changes (the snapshot identity changes — `useShellMountSnapshot`
    // returns a fresh frozen view on each `subscribe()` notification).
    const shortcuts = new Map<string, string>();
    for (const item of mount.getNavItems()) {
      if (item.kbd !== undefined && item.kbd.length > 0) {
        shortcuts.set(item.kbd.toLowerCase(), item.route);
      }
    }

    function onKey(e: KeyboardEvent): void {
      // Guard 3: any modifier disqualifies — keep chord shortcuts reserved.
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      // Guard 1 + 2: focused form fields / editable elements keep their keys.
      const target = e.target as HTMLElement | null;
      if (target !== null) {
        const tag = target.tagName;
        if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;
        if (target.isContentEditable) return;
      }
      // Guard 4: CommandPalette open — its own input box catches keys via Guard 1,
      // but a stray bubbling keydown shouldn't switch surfaces while it's up.
      if (paletteOpen) return;
      // Look up by lowercased key — handles capital letters from shift modifier the
      // same as bare letters. Note: shift IS allowed (the standalone permits it);
      // only Meta/Ctrl/Alt are blocked.
      const route = shortcuts.get(e.key.toLowerCase());
      if (route !== undefined) {
        e.preventDefault();
        navigate(route);
      }
    }

    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [mount, navigate, paletteOpen]);

  return null;
}
